import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface PneuItem {
  id: number;
  codigo: string;
  marca: string;
  modelo: string;
  medida: string;
  dot: string;
  numero_fogo: string;
  veiculo_placa: string;
  posicao: string;
  sulco_inicial: number;
  sulco_atual: number;
  sulco_minimo: number;
  quilometragem_total: number;
  status: string;
  recapagens: number;
  valor_unitario: number;
}

const Tires: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const {
    items: pneus,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<PneuItem>({
    fetchFunction: async (page, limit) => {
      try {
        const response = await apiService.getPneus({ page, limit });

        if (response.success && response.data) {
          const apiPneus = Array.isArray(response.data.pneus) ? response.data.pneus : [];

          return {
            success: true,
            data: {
              items: apiPneus,
              pagination: { total: response.data.pagination?.total || apiPneus.length }
            }
          };
        }
        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar pneus:', error);
        return { success: false, data: [], message: 'Erro ao carregar pneus' };
      }
    },
    itemsPerPage: 10,
    filters: {},
    onError: (error) => { utils.showNotification(error, { type: 'error' }); }
  });

  const getStatusClass = (status: string): string => {
    const map: Record<string, string> = {
      'novo': 'status-info',
      'em_uso': 'status-success',
      'estoque': 'status-secondary',
      'recapagem': 'status-warning',
      'descartado': 'status-danger'
    };
    return map[status] || 'status-secondary';
  };

  const getStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
      'novo': 'Novo',
      'em_uso': 'Em Uso',
      'estoque': 'Estoque',
      'recapagem': 'Recapagem',
      'descartado': 'Descartado'
    };
    return map[status] || status;
  };

  const getSulcoClass = (atual: number, minimo: number): string => {
    const restante = atual - minimo;
    if (restante <= 0) return 'status-danger';
    if (restante <= 2) return 'status-warning';
    return 'status-success';
  };

  const formatCurrency = (value: number): string => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const filteredPneus = pneus.filter(p => {
    const matchesSearch = !filter ||
      (p.marca || '').toLowerCase().includes(filter.toLowerCase()) ||
      (p.modelo || '').toLowerCase().includes(filter.toLowerCase()) ||
      (p.numero_fogo || '').toLowerCase().includes(filter.toLowerCase()) ||
      (p.veiculo_placa || '').toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Gestao de Pneus</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando pneus...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestao de Pneus</h1>
        <button className="btn btn-primary" onClick={() => navigate('/pneus/novo')}>
          <i className="fas fa-plus"></i> Novo Pneu
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Controle de Pneus</h2>
            <FilterPanel
              fields={[
                { type: 'text', label: 'Buscar', value: filter, onChange: setFilter, placeholder: 'Marca, modelo, DOF ou placa...' },
                {
                  type: 'select', label: 'Status', value: statusFilter, onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'novo', label: 'Novo' },
                    { value: 'em_uso', label: 'Em Uso' },
                    { value: 'estoque', label: 'Estoque' },
                    { value: 'recapagem', label: 'Recapagem' },
                    { value: 'descartado', label: 'Descartado' }
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => { setFilter(''); setStatusFilter(''); }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Pneus"
            />
          </div>

          <div className="card-body">
            {filteredPneus.length > 0 && (
              <ResultsCounter displayedItems={displayedItems} totalItems={totalItems} isLoading={isLoading} label="pneus" />
            )}

            {filteredPneus.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-circle-notch empty-state-icon"></i>
                <h3>Nenhum pneu encontrado</h3>
                <p>{filter || statusFilter ? 'Tente ajustar os filtros.' : 'Cadastre o primeiro pneu do sistema.'}</p>
                <button className="btn btn-primary" onClick={() => navigate('/pneus/novo')}>Cadastrar Primeiro Pneu</button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>N. Fogo</th>
                      <th>Marca/Modelo</th>
                      <th>Medida</th>
                      <th>Veiculo</th>
                      <th>Posicao</th>
                      <th>Sulco Atual</th>
                      <th>Sulco Inicial</th>
                      <th>KM Total</th>
                      <th>Recapagens</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPneus.map(pneu => (
                      <tr key={pneu.id}>
                        <td><strong>{pneu.numero_fogo || pneu.codigo || '-'}</strong></td>
                        <td>{pneu.marca} {pneu.modelo}</td>
                        <td>{pneu.medida}</td>
                        <td>{pneu.veiculo_placa || '-'}</td>
                        <td>{pneu.posicao || '-'}</td>
                        <td>
                          <span className={`status-badge ${getSulcoClass(pneu.sulco_atual, pneu.sulco_minimo || 1.6)}`}>
                            {pneu.sulco_atual} mm
                          </span>
                        </td>
                        <td>{pneu.sulco_inicial} mm</td>
                        <td>{new Intl.NumberFormat('pt-BR').format(pneu.quilometragem_total || 0)} km</td>
                        <td>{pneu.recapagens || 0}</td>
                        <td>{formatCurrency(pneu.valor_unitario)}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(pneu.status)}`}>
                            {getStatusLabel(pneu.status)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-view" onClick={() => navigate(`/pneus/visualizar/${pneu.id}`)} title="Visualizar">
                              <i className="fas fa-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LoadMoreButton onClick={loadMore} isLoading={isLoading} hasMore={hasMore} />
              </div>
            )}
          </div>
        </div>

        {/* Estatisticas */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-circle-notch"></i></div>
            <div className="stat-value">{pneus.length}</div>
            <div className="stat-label">Total de Pneus</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-road"></i></div>
            <div className="stat-value">{pneus.filter(p => p.status === 'em_uso').length}</div>
            <div className="stat-label">Em Uso</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-warehouse"></i></div>
            <div className="stat-value">{pneus.filter(p => p.status === 'estoque').length}</div>
            <div className="stat-label">Em Estoque</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-exclamation-triangle"></i></div>
            <div className="stat-value">{pneus.filter(p => (p.sulco_atual - (p.sulco_minimo || 1.6)) <= 2 && p.status === 'em_uso').length}</div>
            <div className="stat-label">Sulco Baixo</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tires;
